/*
 * Copyright © 2025 Gavin Sawyer. All rights reserved.
 */

import { inject, Injectable, type Signal }                                                                                                                        from "@angular/core";
import { toSignal }                                                                                                                                               from "@angular/core/rxjs-interop";
import { type User }                                                                                                                                              from "@angular/fire/auth";
import { collection, collectionData, type CollectionReference, collectionSnapshots, type DocumentReference, Firestore, query, type QueryDocumentSnapshot, where } from "@angular/fire/firestore";
import { type BagDocument }                                                                                                                                       from "@bowstring/interfaces";
import { catchError, map, Observable, of, switchMap }                                                                                                             from "rxjs";
import { AuthenticationService }                                                                                                                                  from "./AuthenticationService";
import { RxSsrService }                                                                                                                                           from "./RxSsrService";


@Injectable({ providedIn: "root" })
export class BagService {

  private readonly authenticationService: AuthenticationService = inject<AuthenticationService>(AuthenticationService);
  private readonly firestore: Firestore                         = inject<Firestore>(Firestore);
  private readonly rxSsrService: RxSsrService                   = inject<RxSsrService>(RxSsrService);

  public readonly bagDocument$: Signal<BagDocument | undefined>                                          = toSignal<BagDocument | undefined>(
    this.authenticationService.userObservable.pipe<BagDocument | undefined>(
      this.rxSsrService.wrap<User, BagDocument | undefined>(
        switchMap<User, Observable<BagDocument | undefined>>(
          ({ uid: userId }: User): Observable<BagDocument | undefined> => collectionData<BagDocument>(
            query<BagDocument, BagDocument>(
              collection(
                this.firestore,
                "bags",
              ) as CollectionReference<BagDocument, BagDocument>,
              where(
                "userId",
                "==",
                userId,
              ),
            ),
          ).pipe<Array<BagDocument> | undefined, BagDocument | undefined>(
            catchError<Array<BagDocument>, Observable<undefined>>(
              (): Observable<undefined> => of<undefined>(undefined),
            ),
            map<Array<BagDocument> | undefined, BagDocument | undefined>(
              (bagDocuments?: Array<BagDocument>): BagDocument | undefined => bagDocuments?.[0],
            ),
          ),
        ),
        "01967ce7-67e2-77f8-b320-8f8e53d1a779",
      ),
    ),
  );
  public readonly bagDocumentReference$: Signal<DocumentReference<BagDocument, BagDocument> | undefined> = toSignal<DocumentReference<BagDocument, BagDocument> | undefined>(
    this.authenticationService.userObservable.pipe<DocumentReference<BagDocument, BagDocument> | undefined>(
      switchMap<User, Observable<DocumentReference<BagDocument, BagDocument> | undefined>>(
        ({ uid: userId }: User): Observable<DocumentReference<BagDocument, BagDocument> | undefined> => (collectionSnapshots<BagDocument>(
          query<BagDocument, BagDocument>(
            collection(
              this.firestore,
              "bags",
            ) as CollectionReference<BagDocument, BagDocument>,
            where(
              "userId",
              "==",
              userId,
            ),
          ),
        ) as Observable<Array<QueryDocumentSnapshot<BagDocument, BagDocument>>>).pipe<Array<QueryDocumentSnapshot<BagDocument, BagDocument>> | undefined, DocumentReference<BagDocument, BagDocument> | undefined>(
          catchError<Array<QueryDocumentSnapshot<BagDocument, BagDocument>>, Observable<undefined>>(
            (): Observable<undefined> => of<undefined>(undefined),
          ),
          map<Array<QueryDocumentSnapshot<BagDocument, BagDocument>> | undefined, DocumentReference<BagDocument, BagDocument> | undefined>(
            (bagDocumentSnapshots?: Array<QueryDocumentSnapshot<BagDocument, BagDocument>>): DocumentReference<BagDocument, BagDocument> | undefined => bagDocumentSnapshots?.[0]?.ref,
          ),
        ),
      ),
    ),
  );

}
